/**
 * Componente genérico de skeleton
 * Implementa DRY (Don't Repeat Yourself) e Single Responsibility
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded' | 'default';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
  shimmer?: boolean;
  lines?: number;
  spacing?: string;
}

/**
 * Componente básico de skeleton
 */
export function Skeleton({
  className,
  variant = 'default',
  width,
  height,
  animation = 'pulse',
  shimmer = false,
  lines = 1,
  spacing = 'space-y-2',
  ...props
}: SkeletonProps) {
  const baseClasses = 'bg-muted';
  const variantClasses = {
    default: 'rounded-md h-4',
    text: 'h-4 w-full rounded-sm',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-lg',
  };
  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-[wave_2s_ease-in-out_infinite]',
    none: '',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = width;
  if (height) style.height = height;

  if (lines === 1) {
    return (
      <div
        className={cn(
          baseClasses,
          variantClasses[variant],
          animationClasses[animation],
          shimmer &&
            'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent',
          className
        )}
        style={style}
        {...props}
      />
    );
  }

  return (
    <div className={cn(spacing, className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={cn(
            baseClasses,
            variantClasses[variant],
            animationClasses[animation],
            shimmer &&
              'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent',
            index === lines - 1 && variant === 'text' && 'w-3/4' // Última linha menor para texto
          )}
          style={style}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton para avatar
 */
export const AvatarSkeleton: React.FC<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}> = ({ size = 'md', className }) => {
  const sizes = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  return <Skeleton variant="circular" className={cn(sizes[size], className)} />;
};

/**
 * Skeleton para texto
 */
export const TextSkeleton: React.FC<{
  lines?: number;
  className?: string;
  spacing?: string;
}> = ({ lines = 3, className, spacing }) => {
  return <Skeleton variant="text" lines={lines} className={className} spacing={spacing} />;
};

/**
 * Skeleton para card
 */
export const CardSkeleton: React.FC<{
  showAvatar?: boolean;
  showImage?: boolean;
  textLines?: number;
  className?: string;
}> = ({ showAvatar = false, showImage = false, textLines = 3, className }) => {
  return (
    <div className={cn('space-y-4 p-4 border rounded-lg', className)}>
      {showImage && <Skeleton className="h-48 w-full" variant="rectangular" />}

      <div className="space-y-3">
        {showAvatar && (
          <div className="flex items-center space-x-3">
            <AvatarSkeleton />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        )}

        <TextSkeleton lines={textLines} />
      </div>
    </div>
  );
};

/**
 * Skeleton para tabela
 */
export const TableSkeleton: React.FC<{
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}> = ({ rows = 5, columns = 4, showHeader = true, className }) => {
  return (
    <div className={cn('space-y-3', className)}>
      {showHeader && (
        <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={`header-${index}`} className="h-4" />
          ))}
        </div>
      )}

      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-4" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton para lista
 */
export const ListSkeleton: React.FC<{
  items?: number;
  showAvatar?: boolean;
  showIcon?: boolean;
  className?: string;
}> = ({ items = 5, showAvatar = false, showIcon = false, className }) => {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="flex items-center space-x-3">
          {showAvatar && <AvatarSkeleton size="sm" />}
          {showIcon && <Skeleton className="h-5 w-5" variant="rounded" />}

          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Skeleton para formulário
 */
export const FormSkeleton: React.FC<{
  fields?: number;
  showButtons?: boolean;
  className?: string;
}> = ({ fields = 4, showButtons = true, className }) => {
  return (
    <div className={cn('space-y-4', className)}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-4 w-1/4" /> {/* Label */}
          <Skeleton className="h-10 w-full" variant="rounded" /> {/* Input */}
        </div>
      ))}

      {showButtons && (
        <div className="flex space-x-2 pt-4">
          <Skeleton className="h-10 w-20" variant="rounded" />
          <Skeleton className="h-10 w-20" variant="rounded" />
        </div>
      )}
    </div>
  );
};

/**
 * Skeleton para dashboard/métricas
 */
export const MetricsSkeleton: React.FC<{
  metrics?: number;
  showChart?: boolean;
  className?: string;
}> = ({ metrics = 4, showChart = true, className }) => {
  return (
    <div className={cn('space-y-6', className)}>
      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: metrics }).map((_, index) => (
          <div key={index} className="p-4 border rounded-lg space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        ))}
      </div>

      {/* Gráfico */}
      {showChart && (
        <div className="p-4 border rounded-lg space-y-4">
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-64 w-full" variant="rounded" />
        </div>
      )}
    </div>
  );
};

/**
 * Hook para gerenciar estados de skeleton
 */
export const useSkeleton = (loading: boolean, delay: number = 0) => {
  const [showSkeleton, setShowSkeleton] = React.useState(false);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (loading) {
      if (delay > 0) {
        timeoutId = setTimeout(() => setShowSkeleton(true), delay);
      } else {
        setShowSkeleton(true);
      }
    } else {
      setShowSkeleton(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, delay]);

  return showSkeleton;
};

/**
 * Componente de skeleton condicional
 */
export const ConditionalSkeleton: React.FC<{
  loading: boolean;
  skeleton: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
}> = ({ loading, skeleton, children, delay = 0 }) => {
  const showSkeleton = useSkeleton(loading, delay);

  return showSkeleton ? <>{skeleton}</> : <>{children}</>;
};

export default Skeleton;
